import { useContactInvalidation } from "@/hooks/use-contact-invalidation";
import {
  createContactAddressV1,
  deleteContactAddressV1,
  updateContactAddressV1,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse, type Contact } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { orderBy } from "lodash";
import toast from "react-hot-toast";
import { type ContactSubitemMutationOptions } from "./contact-subitem-mutation-options";

export const useCreateContactAddress = (
  options?: ContactSubitemMutationOptions,
) => {
  const queryClient = useQueryClient();
  const invalidateContact = useContactInvalidation();
  return useMutation({
    mutationFn: (data: Parameters<typeof createContactAddressV1>[0]) =>
      createContactAddressV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contact, data.contactId],
        (oldData: APIResponse<Contact> | undefined) => {
          if (!oldData) return;
          const oldAddresses = oldData.data.addresses;
          if (data.isPrimary) {
            oldAddresses.forEach((address) => {
              if (address.id !== data.id) {
                address.isPrimary = false;
              }
            });
          }
          return {
            data: {
              ...oldData.data,
              addresses: orderBy(
                [...oldAddresses, data],
                ["isPrimary"],
                ["desc"],
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.contact,
          data.contactId,
        ]),
      };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.contact, variables.contactId],
          context.previousData,
        );
      }
      toast.error("Error creating contact address");
    },
    onSettled: async () => {
      await invalidateContact();
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Contact address created successfully");
      }
    },
  });
};

export const useUpdateContactAddress = (
  options?: ContactSubitemMutationOptions,
) => {
  const invalidateContact = useContactInvalidation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateContactAddressV1>[0]) =>
      updateContactAddressV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contact, data.contactId],
        (oldData: APIResponse<Contact> | undefined) => {
          if (!oldData) return;
          const oldAddresses = oldData.data.addresses;
          if (data.isPrimary) {
            oldAddresses.forEach((address) => {
              if (address.id !== data.id) {
                address.isPrimary = false;
              }
            });
          }
          return {
            data: {
              ...oldData.data,
              addresses: orderBy(
                oldAddresses.map((address) => {
                  return address.id === data.id ? data : address;
                }),
                ["isPrimary"],
                ["desc"],
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.contact,
          data.contactId,
        ]),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.contact, variables.contactId],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating contact address");
    },
    onSettled: async () => {
      await invalidateContact();
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Contact address updated successfully");
      }
    },
  });
};

export const useDeleteContactAddress = (
  options?: ContactSubitemMutationOptions,
) => {
  const queryClient = useQueryClient();
  const invalidateContact = useContactInvalidation();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteContactAddressV1>[0]) =>
      deleteContactAddressV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contact, data.contactId],
        (oldData: APIResponse<Contact> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              addresses: oldData.data.addresses.filter((address) => {
                return address.id !== data.id;
              }),
              ...data,
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.contact,
          data.contactId,
        ]),
      };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.contact, variables.contactId],
          context.previousData,
        );
      }
      toast.error("Error deleting contact address");
    },
    onSettled: async () => {
      await invalidateContact();
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Contact address deleted successfully");
      }
    },
  });
};
